import { type CSSProperties, type FC, type ReactNode, use, useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { Checkbox, cn } from "@heroui/react";
import { type GQL, Utils } from "@binale-tech/shared";
import dayjs from "dayjs";

import { CalendarItemStateControlContext } from "@domain/context/CalendarItemStateContext";
import { documentColorHex } from "@ui-components/hooks/useColorItems";
import type { CalendarEvent, EventCategory, EventGroup } from "@accounting/common/calendar/types";
import { CalendarItemStatePopover } from "./CalendarItemStatePopover";

// SP4 (FIBU-22): shared event-row rendering, extracted out of
// HomeCalendarWidget.tsx so both the widget and the dedicated calendar page
// (CalendarPageView, design spec §IV.4/§IV.7 Part B) render events
// identically instead of maintaining a second copy of the same markup.
// Behavior/markup is unchanged from the widget's original inline
// definitions - this is a pure relocation.

export const GROUP_ORDER: EventGroup[] = ["heute", "woche", "monat", "spaeter"];

export const GROUP_LABEL_ID: Record<EventGroup, string> = {
    heute: "app.home.calendar.group.heute",
    woche: "app.home.calendar.group.woche",
    monat: "app.home.calendar.group.monat",
    spaeter: "app.home.calendar.group.spaeter",
};

export const DOT_CLASS: Record<EventCategory, string> = {
    ustva: "bg-amber-500",
    sondervz: "bg-amber-300",
    "vz-est": "bg-indigo-500",
    "vz-kst": "bg-indigo-500",
    "vz-gewst": "bg-indigo-500",
    annual: "bg-violet-500",
    offenlegung: "bg-teal-500",
    user: "bg-sky-500",
};

export const CategoryDot: FC<{ category: EventCategory; color?: GQL.IDocumentColors }> = ({ category, color }) => {
    const style = useMemo<CSSProperties | undefined>(
        () => (color ? { backgroundColor: documentColorHex[color] } : undefined),
        [color],
    );
    return <span className={cn("mt-1 size-2 shrink-0 rounded-full", !color && DOT_CLASS[category])} style={style} />;
};

// SP3 (FIBU-22): done items stay in place, struck through (design spec
// §III.11 decision #4) - never hidden, so the horizon/grouping logic
// (SP1, unchanged) doesn't need a second display mode.
export const EventLabel: FC<{ event: CalendarEvent }> = ({ event }) => (
    <span className={event.done ? "line-through opacity-50" : undefined}>
        <FormattedMessage id={event.titleId} values={event.titleValues} />
    </span>
);

// SP3 (FIBU-22): the done checkbox - works identically for `source: "user"`
// and `source: "derived"` items, keyed by `stateKey` (design spec §III.7).
// Only shown to writers (same gate SP1 uses for +/edit/delete); toggling
// never touches note/color, so it can't clobber a popover save (§III.6).
export const DoneCheckbox: FC<{ event: CalendarEvent }> = ({ event }) => {
    const intl = useIntl();
    const { setItemState } = use(CalendarItemStateControlContext);
    const handleChange = useCallback(
        (isSelected: boolean) => {
            // `event.source` (SP1's plain CalendarEventSource union) shares its
            // runtime values with the generated `ICalendarItemStateRefType`
            // enum - see the cast note in calendarItemState.ts.
            setItemState({
                refType: event.source as GQL.ICalendarItemStateRefType,
                refId: event.stateKey,
                done: isSelected,
            });
        },
        [setItemState, event.source, event.stateKey],
    );
    return (
        <Checkbox
            aria-label={intl.formatMessage({ id: "app.home.calendar.state.doneLabel" })}
            isSelected={!!event.done}
            onChange={handleChange}
            className="CalendarItemStatePopover__doneCheckbox mt-0.5 shrink-0"
        >
            <Checkbox.Content>
                <Checkbox.Control className={cn({ "border border-border-tertiary": !event.done })}>
                    <Checkbox.Indicator />
                </Checkbox.Control>
            </Checkbox.Content>
        </Checkbox>
    );
};

// User rows (source: "user") open the edit modal on click - derived deadlines either
// deep-link (event.link) or stay a plain informational row; only one of the three ever
// applies for a given event.
export const EventRowShell: FC<{
    event: CalendarEvent;
    onEdit?: (event: CalendarEvent) => void;
    children: ReactNode;
}> = ({ event, onEdit, children }) => {
    const handleEdit = useCallback(() => {
        onEdit?.(event);
    }, [onEdit, event]);

    if (event.link) {
        return (
            <Link to={event.link} className="flex items-start justify-between gap-4 text-app-text hover:opacity-80">
                {children}
            </Link>
        );
    }
    if (event.source === "user" && onEdit) {
        return (
            <button
                type="button"
                onClick={handleEdit}
                className="flex w-full items-start justify-between gap-4 text-left text-app-text hover:opacity-80"
            >
                {children}
            </button>
        );
    }
    return <div className="flex items-start justify-between gap-4 text-app-text">{children}</div>;
};

// SP3 (FIBU-22): the done checkbox and note/color popover sit OUTSIDE
// EventRowShell as siblings, not nested inside it - EventRowShell can render
// as a `<Link>` or `<button>` (edit affordance), and nesting another
// interactive control (checkbox/popover trigger) inside either would be
// invalid HTML and double-fire clicks. Both are gated by `canWriteCalendar`,
// same as SP1's +/edit/delete (design spec §III.7).
export const CollapsedRow: FC<{
    event: CalendarEvent;
    onEdit?: (event: CalendarEvent) => void;
    canWriteCalendar: boolean;
}> = ({ event, onEdit, canWriteCalendar }) => (
    <span className="flex items-center gap-2">
        {canWriteCalendar && <DoneCheckbox event={event} />}
        <span className="min-w-0 flex-1">
            <EventRowShell event={event} onEdit={onEdit}>
                <span className="flex items-center gap-2 text-sm">
                    <CategoryDot category={event.category} color={event.color} />
                    <EventLabel event={event} />
                </span>
                <span className="shrink-0 text-right text-sm tabular-nums opacity-80">
                    {event.amountCents !== undefined && (
                        <span className="mr-2 font-mono font-semibold">
                            {Utils.CurrencyUtils.currencyFormat(event.amountCents)}
                        </span>
                    )}
                    {dayjs(event.date).format("DD.MM.YYYY")}
                </span>
            </EventRowShell>
        </span>
        {canWriteCalendar && <CalendarItemStatePopover event={event} />}
    </span>
);

export const ExpandedRow: FC<{
    event: CalendarEvent;
    onEdit?: (event: CalendarEvent) => void;
    canWriteCalendar: boolean;
}> = ({ event, onEdit, canWriteCalendar }) => (
    <span className="flex items-center gap-2">
        {canWriteCalendar && <DoneCheckbox event={event} />}
        <span className="min-w-0 flex-1">
            <EventRowShell event={event} onEdit={onEdit}>
                <span className="flex items-center gap-2 text-sm">
                    <CategoryDot category={event.category} color={event.color} />
                    <EventLabel event={event} />
                </span>
                <span className="shrink-0 text-right text-xs opacity-80">
                    {event.softDate ? (
                        <span className="flex flex-col items-end gap-0.5">
                            <span>
                                <FormattedMessage
                                    id="app.home.calendar.softDeadline"
                                    values={{ date: dayjs(event.softDate).format("DD.MM.YYYY") }}
                                />
                            </span>
                            <span>
                                <FormattedMessage
                                    id="app.home.calendar.hardDeadline"
                                    values={{ date: dayjs(event.date).format("DD.MM.YYYY") }}
                                />
                            </span>
                        </span>
                    ) : (
                        <span className="flex items-center gap-2">
                            {event.amountCents !== undefined && (
                                <span className="font-mono font-semibold">
                                    {Utils.CurrencyUtils.currencyFormat(event.amountCents)}
                                </span>
                            )}
                            <span>{dayjs(event.date).format("DD.MM.YYYY")}</span>
                        </span>
                    )}
                </span>
            </EventRowShell>
        </span>
        {canWriteCalendar && <CalendarItemStatePopover event={event} />}
    </span>
);
